import React from "react";
import { observer } from "mobx-react";
import { computed } from "mobx";
import { generatePath, withRouter, RouteComponentProps } from "react-router";
import InfiniteScroll from "react-infinite-scroll-component";

import AddIcon from "@material-ui/icons/Add";
import { Button } from "@material-ui/core";

import TipsStore from "stores/TipStore";

import Page from "components/Layout/Page";
import Placeholder from "components/Placeholder";
import FilteringControl from "components/FilteringControl";
import SortingControl from "components/SortingControl";
import withGlobalPopup, {
  GlobalPopupProps,
} from "components/Popup/withGlobalPopup";
import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";

import NavBar from "../NavBar";

import { SNACKBAR_TYPES } from "components/Snackbar";

import ROUTES from "navigation/routes";

import { TableSortType } from "stores/templates/TableStore";

import {
  PageControl,
  SearchContainer,
  SearchControls,
  SearchControlWrapper,
  Search,
  TipCardWrapper,
  OverflowLoader,
  ScrollLoader,
} from "./styles";

import TipCard from "./TipCard";

type Props = RouteComponentProps & GlobalPopupProps & SnackbarContextProps & {};

const HEADER_WITH_TITLE_HEIGHT = "338px";
const MINIMUM_SEARCH_VALUE_LENGTH = 3;

@observer
class Tips extends React.Component<Props> {
  @computed get tips() {
    return TipsStore.items;
  }

  @computed get tipListIsEmpty() {
    return this.tips.length === 0;
  }

  componentDidMount() {
    TipsStore.fetch();
  }

  componentWillUnmount() {
    TipsStore.resetPage();
  }

  fetchMoreTips = async () => {
    const { setMessage } = this.props;

    try {
      await TipsStore.fetchMoreTips();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onSearchApply = (value: string) => {
    if (value.length === 0) {
      this.onSearchClear();
      return;
    }

    TipsStore.applySearch(value);
    value.length >= MINIMUM_SEARCH_VALUE_LENGTH && TipsStore.fetch();
  };

  onSearchClear = () => {
    TipsStore.clearSearch();
    TipsStore.fetch();
  };

  onSortApply = (value: TableSortType) => {
    TipsStore.applyOrder(value);
    TipsStore.resetPage();
    TipsStore.fetch();
  };

  onFilterApply = (
    filterValues: { column: string; value: (string | number)[] }[],
  ) => {
    filterValues.map(filterValue =>
      TipsStore.applyFilter({
        column: filterValue.column,
        value: filterValue.value,
      }),
    );
    TipsStore.resetPage();
    TipsStore.fetch();
  };

  onFilterClear = () => {
    TipsStore.clearFilters();
    TipsStore.fetch();
  };

  onAddTipClick = () => {
    this.props.history.push({
      pathname: generatePath(ROUTES.CONTENT_TIPS_LINK_DETAILS),
      state: { editMode: true },
    });
  };

  onDetailsClick = (tip: any) => () => {
    const path = generatePath(ROUTES.CONTENT_TIPS_LINK_DETAILS, {
      tipId: tip.id,
    });
    this.props.history.push({ pathname: path, state: { editMode: false } });
  };

  onEdit = (tip: any) => () => {
    const path = generatePath(ROUTES.CONTENT_TIPS_LINK_DETAILS, {
      tipId: tip.id,
    });
    this.props.history.push({ pathname: path, state: { editMode: true } });
  };

  onPublish = (tip: any) => async () => {
    const { setMessage } = this.props;
    const { id, publishStatus } = tip;

    try {
      await TipsStore.changeTipStatus(id, publishStatus);
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  render() {
    return (
      <Page
        title="Tips"
        topControls={<NavBar />}
        controls={
          <>
            <PageControl>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={this.onAddTipClick}
              >
                Add Tip
              </Button>
            </PageControl>
          </>
        }
        isLoading={TipsStore.isLoading}
      >
        <SearchContainer>
          <Search
            initValue={TipsStore.search}
            onApply={this.onSearchApply}
            onClear={this.onSearchClear}
          />
          <SearchControls>
            <SearchControlWrapper>
              <SortingControl
                initValue={TipsStore.orderContext.appliedOrderInfo}
                onApply={this.onSortApply}
              />
            </SearchControlWrapper>
            <SearchControlWrapper>
              <FilteringControl
                initValue={TipsStore.filters}
                onApply={this.onFilterApply}
                onClear={this.onFilterClear}
                statusFiltering
              />
            </SearchControlWrapper>
          </SearchControls>
        </SearchContainer>

        {(TipsStore.isTipPublishing || TipsStore.isTipCopying) && (
          <OverflowLoader />
        )}
        {!this.tipListIsEmpty ? (
          <InfiniteScroll
            dataLength={this.tips.length}
            hasMore={TipsStore.hasMoreInfo}
            next={this.fetchMoreTips}
            height={`calc(100vh - ${HEADER_WITH_TITLE_HEIGHT})`}
            loader={<ScrollLoader />}
          >
            {this.tips.map(tip => (
              <TipCardWrapper key={tip.id}>
                <TipCard
                  title={tip.title}
                  image={tip.imageUrl}
                  publishStatus={tip.publishStatus}
                  onDetailsClick={this.onDetailsClick(tip)}
                  onEdit={this.onEdit(tip)}
                  onPublish={this.onPublish(tip)}
                />
              </TipCardWrapper>
            ))}
          </InfiniteScroll>
        ) : (
          <Placeholder
            height={280}
            width={280}
            text={
              TipsStore.search
                ? "No matches found. Please try another search."
                : "No tips added yet."
            }
          />
        )}
      </Page>
    );
  }
}

export default withGlobalPopup(withGlobalSnackbar(withRouter(Tips)));
